import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  patchTargetsById,
  postProfileSync,
  getSourceTables,
  getGetProfilesByIdQueryKey,
} from '@/api/control/control-plane/control-plane';
import {
  SyncRequestSyncMode,
  type ContinuationToken,
  type TargetDto,
  type UpdateTargetRequest,
} from '@/api/control/missionImpactAPI.schemas';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';
import { exceedsIdentifierLimit, isValidTablePrefix } from '@/lib/tablePrefix';

/**
 * Props for the ChangeTablePrefixDialog component.
 */
interface ChangeTablePrefixDialogProps {
  /** The organization ID. */
  orgId: string;
  /** The profile ID. */
  profileId: string;
  /** The target whose table prefix is being changed. */
  target: TargetDto;
  /** The profile's source ID, used to re-validate configured tables against the new prefix. */
  sourceId?: string;
  /**
   * Whether the profile has already triggered its initial sync. When true, changing the prefix
   * is destructive (all target tables are dropped and rebuilt via a ResetAndFull re-sync).
   */
  hasTriggeredInitialSync: boolean;
  /** Whether the dialog is open. */
  open: boolean;
  /** Handler for open-state changes. */
  onOpenChange: (open: boolean) => void;
  /** Optional callback fired after a successful change. */
  onSuccess?: () => void;
}

/** Page size used when enumerating configured source tables for length re-validation. */
const VALIDATION_PAGE_SIZE = 200;

/**
 * Dialog for changing an Azure SQL target's table prefix after profile creation.
 *
 * Changing the prefix is destructive once the profile has synced: every target table (final and
 * staging) is dropped and recreated under the new prefix via a ResetAndFull re-sync. The dialog
 * therefore validates the new prefix as a safe SQL identifier, re-validates every configured table
 * against the 128-character identifier limit under the new prefix (blocking and naming any that
 * would overflow), and requires an explicit acknowledgement before applying.
 */
export function ChangeTablePrefixDialog({
  orgId,
  profileId,
  target,
  sourceId,
  hasTriggeredInitialSync,
  open,
  onOpenChange,
  onSuccess,
}: ChangeTablePrefixDialogProps) {
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const queryClient = useQueryClient();

  const currentPrefix = target.tablePrefix ?? '';
  const [newPrefix, setNewPrefix] = useState(currentPrefix);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offendingTables, setOffendingTables] = useState<string[]>([]);
  const [isApplying, setIsApplying] = useState(false);

  // Re-seed state whenever the dialog is (re)opened for the current target.
  useEffect(() => {
    if (open) {
      setNewPrefix(target.tablePrefix ?? '');
      setAcknowledged(false);
      setError(null);
      setOffendingTables([]);
    }
  }, [open, target.tablePrefix]);

  const normalizedNew = newPrefix.trim();
  const isValid = isValidTablePrefix(newPrefix);
  const isChanged = normalizedNew !== currentPrefix;
  // Acknowledgement is only meaningful once the prefix change is destructive (post initial sync).
  const requiresAcknowledgement = hasTriggeredInitialSync;

  /**
   * Handles open/close, preventing dismissal while an apply is in flight.
   */
  const handleOpenChange = (nextOpen: boolean) => {
    if (isApplying) return;
    onOpenChange(nextOpen);
  };

  /**
   * Fetches every configured source table for the profile's source and returns the logical names
   * whose prefixed staging table name would exceed the SQL identifier limit under the new prefix.
   */
  const findOffendingTables = async (prefix: string): Promise<string[]> => {
    if (!sourceId) return [];

    const offenders: string[] = [];
    let continuationToken: ContinuationToken | undefined = undefined;

    // Page through the full configured-table list so no table is missed.
    do {
      const response = await getSourceTables(orgId, profileId, sourceId, {
        pageSize: VALIDATION_PAGE_SIZE,
        continuationToken,
      });
      const page = response.data;
      const items = page.items ?? [];
      for (const table of items) {
        if (exceedsIdentifierLimit(prefix, table.logicalName)) {
          offenders.push(table.logicalName);
        }
      }
      continuationToken = page.hasMore ? page.continuationToken : undefined;
    } while (continuationToken);

    return offenders;
  };

  /**
   * Applies the prefix change: validates, re-checks table lengths, PATCHes the target, then triggers
   * a ResetAndFull re-sync of the profile when it has already synced.
   */
  const handleApply = async () => {
    setError(null);
    setOffendingTables([]);

    if (!isValid) {
      setError(t.targets.prefixDialog.invalidPrefix);
      return;
    }

    setIsApplying(true);
    try {
      // Block the change if any configured table would exceed the identifier limit under the new prefix.
      const offenders = await findOffendingTables(normalizedNew);
      if (offenders.length > 0) {
        setOffendingTables(offenders);
        setError(t.targets.prefixDialog.tablesTooLong);
        return;
      }

      // PATCH only the prefix; empty string clears it (backend normalizes to no prefix).
      const config: UpdateTargetRequest['config'] = target.$type === 'AzureSql'
        ? { $type: 'AadApp', tablePrefix: normalizedNew }
        : { $type: 'Basic', tablePrefix: normalizedNew };

      await patchTargetsById(orgId, profileId, target.id, { config });

      // Destructive rebuild is only needed once tables already exist (post initial sync).
      if (hasTriggeredInitialSync) {
        await postProfileSync(orgId, profileId, { syncMode: SyncRequestSyncMode.ResetAndFull });
      }

      await queryClient.invalidateQueries({
        queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
      });
      await queryClient.invalidateQueries({
        queryKey: ['targets', 'by-profile', profileId],
      });

      toast.success(t.targets.prefixDialog.updateSuccess);
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      const { message } = handleApiError(err, { context: 'ChangeTablePrefixDialog.apply' }, { showToast: false });
      setError(message);
    } finally {
      setIsApplying(false);
    }
  };

  const canApply =
    isValid &&
    isChanged &&
    !isApplying &&
    (!requiresAcknowledgement || acknowledged);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>{t.targets.prefixDialog.title}</DialogTitle>
          </div>
          <DialogDescription>{t.targets.prefixDialog.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="tablePrefixInput">{t.targets.prefixDialog.label}</Label>
            <Input
              id="tablePrefixInput"
              value={newPrefix}
              onChange={(e) => {
                setNewPrefix(e.target.value);
                setError(null);
                setOffendingTables([]);
              }}
              placeholder={t.targets.prefixDialog.placeholder}
              disabled={isApplying}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">{t.targets.prefixDialog.hint}</p>
          </div>

          {/* Destructive warning shown only when the profile has already synced. */}
          {requiresAcknowledgement && (
            <div className="bg-destructive/10 border-destructive/20 border rounded-md p-4">
              <p className="text-sm text-destructive font-medium mb-2">
                {t.targets.prefixDialog.warningTitle}
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>{t.targets.prefixDialog.warningOperation1}</li>
                <li>{t.targets.prefixDialog.warningOperation2}</li>
                <li>{t.targets.prefixDialog.warningOperation3}</li>
              </ul>
            </div>
          )}

          {offendingTables.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="mb-1">{t.targets.prefixDialog.tablesTooLong}</p>
                <ul className="list-disc list-inside">
                  {offendingTables.map((name) => (
                    <li key={name} className="font-mono text-xs">{name}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {error && offendingTables.length === 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {requiresAcknowledgement && (
            <div className="flex items-start space-x-2">
              <Checkbox
                id="acknowledge-prefix"
                checked={acknowledged}
                onCheckedChange={(checked) => { setAcknowledged(checked === true); }}
                disabled={isApplying}
                className="mt-1"
              />
              <label
                htmlFor="acknowledge-prefix"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                {t.targets.prefixDialog.acknowledge}
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { handleOpenChange(false); }} disabled={isApplying}>
            {t.targets.prefixDialog.cancel}
          </Button>
          <LoadingButton
            variant="destructive"
            onClick={() => { void handleApply(); }}
            disabled={!canApply}
            isLoading={isApplying}
          >
            {t.targets.prefixDialog.apply}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
